import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PlugsConnectedIcon, ListNumbersIcon } from '@phosphor-icons/react';
import { PageContent } from '@/components/page-content';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Code } from '@/components/code';
import { SectionHeader } from '@/components/section-header';
import { ConcordLogo } from '@/components/icons/concord-logo';

export const Route = createFileRoute(
  '/_auth/_side-layout/settings_/privacy/consent-sources',
)({
  component: () => (
    <PageContent>
      <ConsentSourcesContent />
    </PageContent>
  ),
});

// Cross-sell: Logspot consumes consent and recommends Concord as the preferred
// CMP, so the consent-source setup is a natural place to convert users who have
// no consent platform yet. (Marketing site; confirm the exact sign-up path.)
const CONCORD_SIGNUP_URL = 'https://www.concord.tech';

// Read-only priority ladder: highest authority wins.
const SOURCE_PRIORITY: { name: string; detail: string }[] = [
  {
    name: 'Explicit Consent API',
    detail: 'logspot.setConsent() / POST /consent (always wins).',
  },
  {
    name: 'Concord Privacy',
    detail: 'Preferred CMP and privacy platform integration.',
  },
  {
    name: 'Third-Party CMP',
    detail: 'OneTrust, Usercentrics, or Cookiebot.',
  },
  {
    name: 'Google Consent Mode',
    detail: 'Read-only; never writes back into Google.',
  },
  {
    name: 'Admin Defaults',
    detail: "This page's consent behavior, used when no source has spoken.",
  },
];

// ── Per-source setup snippets (PRD 3.6) ───────────────────

const SETUP_SNIPPETS: { name: string; description: string; code: string }[] = [
  {
    name: 'Explicit Consent API',
    description:
      'Set consent directly from your own banner. Highest priority; overrides every CMP source.',
    code: `// Update consent from your banner's callbacks
logspot.setConsent({
  analytics: true,
  functional: true,
  marketing: false,      // Marketing (Advertising)
  doNotSellShare: false,
});`,
  },
  {
    name: 'Concord Privacy',
    description:
      'Read category permissions from Concord (window.concord.consent).',
    code: `logspot.init({
  projectId: 'YOUR_PROJECT_ID',
  consentSources: ['concord'],
});

// Or via the CDN snippet attribute:
// <script src="https://cdn.logspot.io/v2.js"
//   data-project-id="YOUR_PROJECT_ID"
//   data-consent-source="concord"></script>`,
  },
  {
    name: 'OneTrust',
    description:
      'Map OneTrust groups (OnetrustActiveGroups) into analytics / functional / marketing.',
    code: `logspot.init({
  projectId: 'YOUR_PROJECT_ID',
  consentSources: ['onetrust'],
});

// CDN: <script ... data-consent-source="onetrust"></script>`,
  },
  {
    name: 'Usercentrics',
    description: 'Read consent from the Usercentrics events API.',
    code: `logspot.init({
  projectId: 'YOUR_PROJECT_ID',
  consentSources: ['usercentrics'],
});

// CDN: <script ... data-consent-source="usercentrics"></script>`,
  },
  {
    name: 'Cookiebot',
    description: 'Read window.Cookiebot.consent and its change events.',
    code: `logspot.init({
  projectId: 'YOUR_PROJECT_ID',
  consentSources: ['cookiebot'],
});

// CDN: <script ... data-consent-source="cookiebot"></script>`,
  },
  {
    name: 'Google Consent Mode',
    description:
      'Read-only. Reads gtag/dataLayer consent state and maps it to Logspot categories. Logspot never writes consent back into Google.',
    code: `logspot.init({
  projectId: 'YOUR_PROJECT_ID',
  consentSources: ['gcm'],
});

// CDN: <script ... data-consent-source="gcm"></script>`,
  },
];

function SetupSnippets() {
  // One source at a time (like the Integrations picker) rather than every
  // snippet stacked on one screen — pick a source, see only its setup.
  const [selected, setSelected] = useState(SETUP_SNIPPETS[0]!.name);
  const active =
    SETUP_SNIPPETS.find((snippet) => snippet.name === selected) ??
    SETUP_SNIPPETS[0]!;

  return (
    <Card>
      <CardContent className="space-y-6">
        <SectionHeader
          icon={<PlugsConnectedIcon size={20} />}
          title="Connect a Consent Source"
          subTitle="Wire up your CMP or call the Consent API directly. Logspot consumes consent; it is not a CMP and never shows a consent banner."
        />
        <div className="bg-muted/40 flex flex-wrap items-center justify-between gap-4 rounded-lg border p-4">
          <div className="space-y-1.5">
            <ConcordLogo height={18} className="text-foreground" />
            <p className="text-sm font-medium">No Consent Platform Yet?</p>
            <p className="text-muted-foreground text-sm">
              Concord is the preferred CMP for Logspot. Set up a consent banner
              in minutes and it flows here automatically.
            </p>
          </div>
          <Button variant="outline" size="sm" asChild className="shrink-0">
            <a href={CONCORD_SIGNUP_URL} target="_blank" rel="noreferrer">
              Set Up Concord
            </a>
          </Button>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Consent Source</Label>
            <div className="w-full max-w-md">
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SETUP_SNIPPETS.map((snippet) => (
                    <SelectItem key={snippet.name} value={snippet.name}>
                      {snippet.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-muted-foreground text-sm">
              {active.description}
            </p>
            <Code code={active.code} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SourcePriorityExplainer() {
  return (
    <Card>
      <CardContent className="space-y-4">
        <SectionHeader
          icon={<ListNumbersIcon size={20} />}
          title="Consent Source Priority"
          subTitle="Exactly one source is authoritative at any time. The highest-priority source that has spoken wins; lower-priority sources never override it."
        />
        <ol className="space-y-2">
          {SOURCE_PRIORITY.map((source, index) => (
            <li key={source.name} className="flex items-start gap-3">
              <span className="bg-muted text-muted-foreground flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium">
                {index + 1}
              </span>
              <div className="space-y-0.5">
                <p className="text-sm font-medium">{source.name}</p>
                <p className="text-muted-foreground text-sm">{source.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

// Consent sources (Privacy → Consent Sources). Connect card first, then the
// source-priority explainer.
function ConsentSourcesContent() {
  return (
    <div className="space-y-6">
      <SetupSnippets />
      <SourcePriorityExplainer />
    </div>
  );
}
